import { Component, ChangeDetectionStrategy, EventEmitter, Output } from '@angular/core';
import { NavigationExtras, Router } from '@angular/router';

import { TranslatePipe } from '@ngx-translate/core';
import { TaskComponent } from '../../@commons/@components/task/task.component';
import { TaskChecking } from '../../@commons/@components/task/task-checking';
import { LabelPipe } from '../../@commons/pipes/LabelPipe';
import { VwDatePipe } from '../../@commons/pipes/vw-date.pipe';
import { SharedModule } from '../../@commons/ui-components/shared.module';
import { ArticleComponent } from '../../@commons/@components/article/article.component';
import { GardenTaskSheetComponent } from '../../@commons/@components/garden-task-sheet/garden-task-sheet.component';
import { UndoSnackbarComponent } from '../../@commons/ui-components/undo-snackbar/undo-snackbar.component';
import { PlantActionRowComponent } from '../plant-action-row/plant-action-row.component';
import { StorageService } from '../../@commons/@services/front/storage.service';
import {
  TaskListResponse
} from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/location/task-list.response';
import { PlantListResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/location/plant-list.response';
import { ArticleListRequest } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/requests/article/article-list.request';
import { ArticleApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/article/article-api';
import RoutesConstants from '../../@commons/constants/routes.constants';
import { TASK_EMOJIS } from '../../@commons/constants/garden-task.constants';
import { GardenTodo, PlantAction } from '../../@commons/@services/data/garden-todo.store';
import { openTasks, taskSection } from '../../@commons/utils/garden-todo.util';
import { parseLocalDate } from '../../@commons/utils/local-date.util';
import { AgendaEmptyComponent } from '../agenda-empty/agenda-empty.component';
import { AGENDA_ARTICLES_COUNT } from '../agenda.constants';

/**
 * « Mes tâches », onglet « À faire » (maquettes 02, 03 et 05) : les plantes
 * qui attendent un geste, les gestes de la quinzaine, ceux qui vont passer,
 * puis « À venir » replié ; les articles dessous.
 *
 * Cocher barre la ligne et propose « Annuler » (voir `TaskChecking`) ; à la
 * fermeture du message, la ligne quitte la liste. L'étape d'une plante ne part
 * qu'à la fermeture du message, pour ne fausser ni sa date ni son suivi de
 * croissance (décision 14).
 */
@Component({
    selector: 'vw-agenda-task',
    imports: [SharedModule, TranslatePipe, LabelPipe, VwDatePipe, TaskComponent, ArticleComponent, AgendaEmptyComponent,
      PlantActionRowComponent, GardenTaskSheetComponent, UndoSnackbarComponent],
    templateUrl: './agenda-task.component.html',
    styleUrl: './agenda-task.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    providers: [{
            provide: ArticleApi,
            useClass: ArticleApi
        }]
})
export class AgendaTaskComponent extends TaskChecking {
  /** Ce qui reste à faire, à chaque changement : le sous-titre de l'écran. */
  @Output() countChange = new EventEmitter<number>();

  todo: GardenTodo | null = null;
  articles: any[] = []; // Array to hold badge information
  isLoading: boolean = false;
  readonly emojis = TASK_EMOJIS;
  /** L'influence lunaire du jour, pour la feuille. */
  todayInfluence: string | null = null;
  upcomingOpen = false;

  constructor(private storageService: StorageService, private articleApi: ArticleApi, private router: Router) {
    super();
  }

  async ngOnInit() {
    this.isLoading = true;
    void this.store.lunarInfluenceToday().then((influence) => (this.todayInfluence = influence));
    try {
      this.loadTodo();
      await this.loadArticles();
    } catch (e) {
      console.error('AgendaTaskComponent: failed to initialize', e);
    } finally {
      this.isLoading = false;
    }
  }

  loadTodo() {
    return this.store.load()
      .then((todo) => {
        this.todo = todo;
        this.countChanged();
      })
      .catch((e) => console.error('AgendaTaskComponent: failed to load tasks', e));
  }

  async loadArticles() {
    const articles = await this.articleApi.list(new ArticleListRequest(), 1, AGENDA_ARTICLES_COUNT, this.storageService.getCurrentLanguage());
    this.articles = articles.results;
  }

  get plantActions(): PlantAction[] {
    return (this.todo?.plantActions ?? []).filter((action) => !this.gone.has(action.plant.uid));
  }

  /** Les gestes de la quinzaine : ceux en cours d'abord, ceux en marge ensuite, chacun par fin la plus proche ; une seule liste, sans rubrique à part. */
  get currentTasks(): TaskListResponse[] {
    return this.openTasks;
  }

  /** Les gestes de la quinzaine suivante, du plus proche au plus lointain. */
  get upcomingTasks(): TaskListResponse[] {
    return this.tasks
      .filter((task) => taskSection(task, this.today) === 'upcoming')
      .sort((a, b) => parseLocalDate(a.startDate)!.getTime() - parseLocalDate(b.startDate)!.getTime());
  }

  /** Rien à montrer : l'état vide, jamais sur une panne de chargement. */
  get isEmpty(): boolean {
    return !!this.todo && !this.plantActions.length && !this.openTasks.length && !this.upcomingTasks.length;
  }

  /**
   * Une plante cochée attend la fermeture du message. Si l'étape suivante
   * demande « combien, où ? » (repiquage, plantation), la fiche ouvre ce même
   * parcours, comme son « C'est fait ? ».
   */
  onPlantChecked(action: PlantAction) {
    const uid = action.plant.uid;
    if (this.checked.has(uid)) return;
    if (action.nextStep.triggersRedistribution) {
      this.openPlant(action.plant, { queryParams: { action: 'confirm-step' } });
      return;
    }
    this.settle();
    this.check(uid);
    this.pendingUndo = {
      key: uid,
      message: 'tasks.undo.done',
      commit: () => this.store.changePlantStep(action.plant.uid, action.nextStep),
    };
  }

  /** La fiche de la plante, sur sa propre variété : celle d'où viennent ses étapes. */
  openPlant(plant: PlantListResponse, extras: NavigationExtras = {}) {
    void this.router.navigate(
      ['/garden/cultivations', plant.cultivationUid, 'variants', plant.cultivationVariant.uid, 'plants', plant.uid],
      extras,
    );
  }

  onAllArticles() {
    this.router.navigate([RoutesConstants.ARTICLES]);
  }

  private get tasks(): TaskListResponse[] {
    return this.notGone(this.todo?.tasks ?? []);
  }

  private get openTasks(): TaskListResponse[] {
    return openTasks(this.tasks, this.today);
  }

  /** Le compte de « Mes tâches », sans les lignes cochées ni parties. */
  protected override countChanged() {
    if (this.todo) this.countChange.emit(this.countLeft(this.todo));
  }
}
